import { useCallback, useRef, useState } from 'react';
import invariant from 'tiny-invariant';
import { useUpdateEffect } from '../hooks';
import { CLEAR_ALL_VALUE } from './constants';
export function useList(props) {
  const {
    onSelect,
    selectionMode = 'none',
    onClick: propsOnClick,
    children,
    selectedValues,
    withResetButton = false,
    defaultSelected,
    size = 'l',
    label,
    resetButtonText
  } = props;
  const dirtyRef = useRef(false);
  const [selectedSet, setSelectedSet] = useState(() => new Set(arrayify(selectedValues)));
  const [defaultSelectedSet, setDefaultSelectedSet] = useState(() => new Set(arrayify(defaultSelected)));
  useUpdateEffect(() => {
    setSelectedSet(new Set(arrayify(selectedValues)));
  }, [selectedValues]);
  useUpdateEffect(() => {
    setDefaultSelectedSet(new Set(arrayify(defaultSelected)));
  }, [defaultSelected]);
  let currentSelectedValues = selectedSet;
  if (selectedSet.size === 0 && defaultSelectedSet.size !== 0 && !dirtyRef.current) currentSelectedValues = defaultSelectedSet;
  const onSelectionChange = useCallback(keys => {
    dirtyRef.current = true;
    let nextSelected = '';
    switch (selectionMode) {
      case 'single':
        invariant(keys.length < 2, `
              Multiselection isn't supported in single selection mode. 
              You're should either remove sublists or set selection mode to multiple
            `);
        // eslint-disable-next-line prefer-destructuring
        nextSelected = keys[0];
        break;
      case 'multiple':
        if (currentSelectedValues.size !== 0) {
          const clonedSelectedSet = new Set(currentSelectedValues);
          keys.forEach(key => {
            if (clonedSelectedSet.has(key)) {
              clonedSelectedSet.delete(key);
            } else {
              clonedSelectedSet.add(key);
            }
          });
          nextSelected = Array.from(clonedSelectedSet);
        } else {
          nextSelected = keys;
        }
        break;
      default:
        return;
    }
    onSelect == null ? void 0 : onSelect(nextSelected);
  }, [currentSelectedValues, onSelect, selectionMode]);
  const onClick = useCallback(value => {
    if (value === CLEAR_ALL_VALUE) {
      dirtyRef.current = false;
    }
    if (value === CLEAR_ALL_VALUE && selectionMode !== 'none') {
      onSelect == null ? void 0 : onSelect(selectionMode === 'single' ? null : []);
    } else {
      propsOnClick == null ? void 0 : propsOnClick(value);
    }
  }, [propsOnClick, onSelect, selectionMode]);
  return {
    listAttributes: {
      tabIndex: 0,
      role: selectionMode === 'none' ? 'list' : 'listbox',
      'aria-label': label,
      'aria-multiselectable': selectionMode === 'multiple'
    },
    listProviderValue: {
      onClick,
      selectionMode,
      selectedValues: currentSelectedValues,
      onSelect: onSelectionChange,
      selectable: selectionMode !== 'none',
      size
    },
    children,
    resetButton: withResetButton ? {
      value: CLEAR_ALL_VALUE,
      text: resetButtonText != null ? resetButtonText : 'Сбросить'
    } : null
  };
}
function arrayify(value) {
  let result = [];
  if (value != null) {
    return Array.isArray(value) ? value : [value];
  }
  return result;
}